import React, { useCallback, useMemo, useState } from 'react';
import { TabMenu } from '../../../../../ud-ui/components/tab-menu';
import StageInfoForm from './stage-info-form';
import TeamGroupsForm from './team-groups/team-groups-form';
import { ChampionshipStateData } from '../../../../store/championship';
import StageMatches from './matches/stage-matches';
import { useAcceptedChampionshipTeams } from '../../../hooks/use-accepted-championship-teams';
import { GameResult } from '../../../../../games/ui/components/game-result';
import StageGrid from './stage-grid/stage-grid';
import { useSelector } from 'react-redux';
import { selectedStageSelector } from '../../../../store/stages';

const defaultTabs = ['Редактирование', 'Команды', 'Сетка матчей', 'Календарь матчей'];

const extractTeamName = (gameTeam: { name: string; } | null | undefined): string => {
  if (!gameTeam) {
    return 'Неизвестно';
  }

  return gameTeam.name;
};

type StagesFormProps = {
  championship: ChampionshipStateData;
};

const StageForms = ({ championship }: StagesFormProps) => {
  const { teams } = useAcceptedChampionshipTeams();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedGameId, selectGame] = useState<number | null>(null);

  const stage = useSelector(selectedStageSelector);

  const {
    id: championshipId,
    tournamentId,
  } = championship;

  const tabs: string[] = useMemo(() => {
    if (!selectedGameId) {
      return defaultTabs;
    }

    const game = stage?.matches.find((match) => match.id === selectedGameId);
    if (game) {
      const [firstTeam, secondTeam] = game.teams || [];

      return [
        ...defaultTabs,
        `Матч: ${extractTeamName(firstTeam)} - ${extractTeamName(secondTeam)}`,
      ];
    }

    return defaultTabs;
  }, [selectedGameId, stage?.matches]);

  const handleChangeActiveTab = useCallback((index: number) => {
    if (!stage) {
      return;
    }

    setActiveTab(index);
    if (index !== 4) {
      selectGame(null);
    }
  }, [stage, setActiveTab]);

  const handleSelectGameId = useCallback((gameId: number) => {
    selectGame(gameId);
    setActiveTab(4);
  }, []);

  const formNode = useMemo(() => {
    if (stage && activeTab === 1) {
      return (<TeamGroupsForm stage={stage} allTeams={teams || []} />);
    }

    if (stage && activeTab === 2) {
      return (
        <StageGrid stage={stage} />
      );
    }

    if (stage && activeTab === 3) {
      return (
        <StageMatches
          stage={stage}
          championshipId={championshipId}
          tournamentId={tournamentId}
          onShowResultsClick={handleSelectGameId}
        />
      );
    }

    if (stage && selectedGameId && activeTab === 4) {
      return (
        <GameResult gameId={selectedGameId} />
      );
    }

    return (
      <StageInfoForm
        championshipId={championshipId}
        stage={stage}
      />
    );
  }, [stage, activeTab, championshipId, teams, selectedGameId, tournamentId, handleSelectGameId]);

  return (
    <div>
      <div className="mb-5">
        <TabMenu
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleChangeActiveTab}
        />
      </div>
      <div className="container">
        {formNode}
      </div>
    </div>
  );
};

export default StageForms;
